import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Polygon, useGoogleMap } from '@react-google-maps/api';
import {
  setOffMarketHoverPropertyId
} from 'bundles/connect/components/store/actions/ui';
import { setOffMarketPropertyId } from 'bundles/connect/components/store/actions/off_market_property';
import { customPropertyMarkerContent } from '../../../../helpers/map_helper';
import type { AllOffMarketResults } from 'bundles/connect/types';

function highlight(markerView, restricted = false) {
  markerView.content.classList.add(restricted ? 'highlight-restricted' : 'highlight');
  markerView.element.style.zIndex = 1;
}

function unhighlight(markerView, restricted = false) {
  markerView.content.classList.remove(restricted ? 'highlight-restricted' : 'highlight');
  markerView.element.style.zIndex = 0;
}

type OffMarketMapMarkerProps = {
  isHovering: boolean;
  largeIcon: boolean;
  result: AllOffMarketResults;
  setOffMarketPropertyId: (id?: string) => void;
  setOffMarketHoverPropertyId: (id?: string) => void;
}

function OffMarketMapMarker({
  isHovering,
  largeIcon,
  result,
  setOffMarketPropertyId,
  setOffMarketHoverPropertyId
}: OffMarketMapMarkerProps) {
  const map = useGoogleMap();
  const marker = useRef<google.maps.marker.AdvancedMarkerElement>();

  useEffect(() => {
    const markerElementContent = customPropertyMarkerContent(
      result.propertyTypeId,
      largeIcon,
      result.restricted,
    );
    if (marker.current) {
      marker.current.content = markerElementContent;
    }
  }, [largeIcon]);

  useEffect(() => {
    const markerElementContent = customPropertyMarkerContent(
      result.propertyTypeId,
      largeIcon,
      result.restricted,
    );
    // Coerce to numbers and bail on anything missing or non-numeric.
    const lat = Number(result.latitude);
    const lng = Number(result.longitude);

    if (!lat || !lng || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const position = { lat, lng };

    const advancedMarkerView = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: position,
      content: markerElementContent,
      gmpClickable: true
    });
    marker.current = advancedMarkerView;
    const element = advancedMarkerView.element;

    ['focus', 'pointerenter'].forEach((eventName) => {
      element.addEventListener(eventName, () => {
        setOffMarketHoverPropertyId(result.propertyId);
      });
    });

    ['blur', 'pointerleave'].forEach((eventName) => {
      element.addEventListener(eventName, () => {
        setOffMarketHoverPropertyId();
      });
    });

    advancedMarkerView.addListener('gmp-click', () => {
      setOffMarketHoverPropertyId();
      setOffMarketPropertyId(result.propertyId);
    });


    return () => {
      if (marker.current) {
        marker.current.map = null;
      }
    };
  }, [result.propertyId]);

  useEffect(() => {
    if (marker.current) {
      if (isHovering) {
        highlight(marker.current, result.restricted);
      } else {
        unhighlight(marker.current, result.restricted);
      }
    }
  }, [isHovering]);

  if (!result.parcelBoundary) return null;

  return (
    <Polygon
      options={{
        strokeColor: '#5A75D4',
        strokeOpacity: 0.85,
        strokeWeight: 2,
        fillOpacity: 0.2,
        fillColor: '#5A75D4'
      }}
      path={result.parcelBoundary}
    />
  );
}

const mapStateToProps = ({
  results: { totalOffMarketResults },
  ui: { offMarketHoverPropertyId, mapZoomReadonly }
}, ownProps) => ({
  isHovering: offMarketHoverPropertyId === ownProps.result.propertyId,
  largeIcon: mapZoomReadonly >= 15 || (totalOffMarketResults || 0) <= 250
});

const mapActionsToProps = { setOffMarketPropertyId, setOffMarketHoverPropertyId };

export default connect(mapStateToProps, mapActionsToProps)(OffMarketMapMarker);
